import { useEffect, useState } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { StatusDropdownOption } from './StatusDropdown';
import { DealStatus } from '../../../store/investor/reducer';
import { OptionWithMeta, ShowOnlyNextStatusConfig } from '../../../types/statusTimeline';
import { STATUS_TYPE } from '../../../store/investor/constants';
import { DEAL_STATUS_FIELD } from '../DataTable/DataTable.constants';

/**
 * Applies showOnlyNextStatus config to reduce dropdown options. Uses row.mandateState to
 * determine DEAL vs MANDATE; when config is true returns next option by index plus default
 * options, when false returns only defaultOption === true. Returns null when config does not apply.
 *
 * @param dropdownOptions - Full list of status options
 * @param selectedOptionIndex - Index of the currently selected option
 * @param dealStatusConfig - Config containing showOnlyNextStatus.DEAL and .MANDATE
 * @param row - Optional row data; mandateState identifies mandate row (use MANDATE config)
 * @returns Filtered options array, or null to use full dropdownOptions
 */
const getNextStatusOptions = (
    dropdownOptions: OptionWithMeta[],
    selectedOptionIndex: number,
    dealStatusConfig: ShowOnlyNextStatusConfig,
    row?: Record<string, unknown> | null,
): OptionWithMeta[] | null => {
    const nextStatusConfig = dealStatusConfig?.showOnlyNextStatus;
    if (!nextStatusConfig) return null;

    const isMandateRow = row != null && row.mandateState !== undefined && row.mandateState !== null;
    const configKey = isMandateRow ? STATUS_TYPE.MANDATE : STATUS_TYPE.DEAL;
    const configValue = nextStatusConfig[configKey];

    if (configValue === undefined) return null;

    const defaultOptions = dropdownOptions.filter((o) => o?.defaultOption === true);

    if (configValue === false) {
        return defaultOptions;
    }
    const nextOption = selectedOptionIndex + 1 < dropdownOptions.length ? dropdownOptions[selectedOptionIndex + 1] : undefined;

    return nextOption ? [nextOption, ...defaultOptions.filter((o) => o.value !== nextOption.value)] : defaultOptions;
};

export const useCustomFilteringLogic = (
    searchTerm,
    field,
    dropdownOptions,
    restrictBackwardMovement,
    selectedOptionIndex,
    row?: Record<string, unknown> | null,
) => {
    const dealStatusConfig = useAppSelector((state) => state.investor.deals.dealStatusConfig);

    const [filteredOptions, setFilteredOptions] = useState<StatusDropdownOption[]>([]);

    useEffect(() => {
        updateFilterActionsBackwardMovement();
    }, [dropdownOptions, restrictBackwardMovement, selectedOptionIndex, searchTerm, dealStatusConfig, row, field]);

    const isDealInLastStage = () => {
        return (
            field === DEAL_STATUS_FIELD &&
            [
                DealStatus.FIRST_DEAL_DISBURSED,
                DealStatus.ALL_UNRESPONSIVE,
                DealStatus.ALL_PASSED,
                DealStatus.DISBURSED,
                DealStatus.UNRESPONSIVE,
                DealStatus.PASSED,
            ].includes(dropdownOptions?.[selectedOptionIndex]?.value)
        );
    };

    const updateFilterActionsBackwardMovement = () => {
        if (isDealInLastStage()) {
            setFilteredOptions([]);
            return;
        }

        const nextStatusOptions =
            field === DEAL_STATUS_FIELD
                ? getNextStatusOptions(
                      dropdownOptions as OptionWithMeta[],
                      selectedOptionIndex,
                      dealStatusConfig as ShowOnlyNextStatusConfig,
                      row,
                  )
                : null;

        const baseOptions = nextStatusOptions ?? dropdownOptions;

        const lowerSearch = searchTerm.toLowerCase();

        const valueIndexMap = new Map(dropdownOptions.map((o, i) => [o.value, i]));
        setFilteredOptions(
            baseOptions.filter((option) => {
                const originalIndex = valueIndexMap.get(option.value) ?? -1;

                if (restrictBackwardMovement && originalIndex <= selectedOptionIndex) {
                    return false;
                }

                return option?.label?.toLowerCase().includes(lowerSearch) || false;
            }),
        );
    };

    return { filteredOptions };
};
